import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Skeleton } from './ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import {
  Sparkles, Calendar, Clock, User, Building, Mail, Phone,
  FileText, MessageSquare, CheckSquare, TrendingUp, History,
  Target, AlertCircle, Lightbulb, RefreshCw, ExternalLink,
  DollarSign, Briefcase, Heart
} from 'lucide-react';
import api from '../api';

// Talking Point Card
const TalkingPointCard = ({ point, index }) => {
  const priorityColors = {
    high: 'border-red-200 bg-red-50',
    medium: 'border-amber-200 bg-amber-50',
    low: 'border-blue-200 bg-blue-50'
  };

  return (
    <div className={`p-3 border rounded-lg ${priorityColors[point.priority] || priorityColors.medium}`}>
      <div className="flex items-start gap-2">
        <span className="flex-shrink-0 w-6 h-6 bg-white rounded-full flex items-center justify-center text-xs font-bold">
          {index + 1}
        </span>
        <div className="flex-1">
          <p className="font-medium text-sm">{point.topic}</p>
          {point.context && (
            <p className="text-xs text-muted-foreground mt-1">{point.context}</p>
          )}
          {point.suggested_question && (
            <p className="text-xs text-blue-600 mt-2 italic">
              ""{point.suggested_question}""
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Activity Timeline Item
const ActivityItem = ({ activity }) => {
  const typeIcons = {
    email: Mail,
    call: Phone,
    meeting: Calendar,
    note: FileText,
    task: CheckSquare,
    deal: DollarSign
  };
  const Icon = typeIcons[activity.type] || MessageSquare;

  return (
    <div className="flex items-start gap-3 py-2">
      <div className="flex-shrink-0 w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
        <Icon className="h-4 w-4 text-slate-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{activity.title}</p>
        <p className="text-xs text-muted-foreground">
          {formatDistanceToNow(parseISO(activity.date), { addSuffix: true })}
        </p>
        {activity.summary && (
          <p className="text-xs text-slate-600 mt-1 line-clamp-2">{activity.summary}</p>
        )}
      </div>
    </div>
  );
};

// Main Smart Meeting Prep Component
export const SmartMeetingPrep = ({ meeting, contactId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [prepData, setPrepData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchMeetingPrep = useCallback(async () => {
    if (!meeting?.id && !contactId) return;
    
    setLoading(true);
    try {
      const response = await api.get('/scheduling/meeting-prep', {
        params: {
          meeting_id: meeting?.id,
          contact_id: contactId || meeting?.contact_id
        }
      });
      setPrepData(response.data);
    } catch (error) {
      console.error('Failed to fetch meeting prep:', error);
      // Generate fallback data if API fails
      setPrepData({
        contact: meeting?.contact || null,
        talking_points: [],
        recent_activities: [],
        open_items: [],
        relationship_summary: null
      });
    } finally {
      setLoading(false);
    }
  }, [meeting, contactId]);

  useEffect(() => {
    fetchMeetingPrep();
  }, [fetchMeetingPrep]);

  if (loading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const { contact, talking_points, recent_activities, open_items, relationship_summary } = prepData || {};

  return (
    <div className="space-y-4">
      {/* Meeting Header */}
      {meeting && (
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold">{meeting.title}</h3>
                <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {meeting.scheduled_datetime && format(parseISO(meeting.scheduled_datetime), 'MMM d, yyyy')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {meeting.scheduled_datetime && format(parseISO(meeting.scheduled_datetime), 'h:mm a')}
                  </span>
                </div>
              </div>
              {meeting.meeting_link && (
                <Button size="sm" variant="outline" asChild>
                  <a href={meeting.meeting_link} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Join
                  </a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact Overview */}
      {contact && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-lg font-bold">
                {(contact.name || contact.first_name || 'C').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">
                  {contact.name || `${contact.first_name} ${contact.last_name}`}
                </h3>
                {contact.title && (
                  <p className="text-sm text-muted-foreground">{contact.title}</p>
                )}
                {contact.company_name && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Building className="h-3 w-3" />
                    {contact.company_name}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-2">
                  {contact.email && (
                    <a href={`mailto:${contact.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      Email
                    </a>
                  )}
                  {contact.phone && (
                    <a href={`tel:${contact.phone}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      Call
                    </a>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for Different Prep Sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview" className="text-xs">
            <Target className="h-3 w-3 mr-1" />
            Talking Points
          </TabsTrigger>
          <TabsTrigger value="history" className="text-xs">
            <History className="h-3 w-3 mr-1" />
            History
          </TabsTrigger>
          <TabsTrigger value="action" className="text-xs">
            <CheckSquare className="h-3 w-3 mr-1" />
            Open Items
          </TabsTrigger>
        </TabsList>

        {/* Talking Points Tab */}
        <TabsContent value="overview" className="mt-4">
          <ScrollArea className="h-[300px] pr-2">
            {talking_points && talking_points.length > 0 ? (
              <div className="space-y-3">
                {talking_points.map((point, idx) => (
                  <TalkingPointCard key={idx} point={point} index={idx} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Lightbulb className="h-10 w-10 mx-auto text-slate-200 mb-3" />
                <p className="text-sm text-muted-foreground">
                  No specific talking points generated
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Build more interaction history to get AI suggestions
                </p>
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          <ScrollArea className="h-[300px] pr-2">
            {recent_activities && recent_activities.length > 0 ? (
              <div className="divide-y">
                {recent_activities.map((activity, idx) => (
                  <ActivityItem key={idx} activity={activity} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="h-10 w-10 mx-auto text-slate-200 mb-3" />
                <p className="text-sm text-muted-foreground">
                  No recent interaction history
                </p>
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Open Items Tab */}
        <TabsContent value="action" className="mt-4">
          <ScrollArea className="h-[300px] pr-2">
            {open_items && open_items.length > 0 ? (
              <div className="space-y-2">
                {open_items.map((item, idx) => (
                  <div key={idx} className="p-3 border rounded-lg flex items-start gap-2">
                    {item.type === 'task' ? (
                      <CheckSquare className="h-4 w-4 text-blue-500 mt-0.5" />
                    ) : item.type === 'deal' ? (
                      <DollarSign className="h-4 w-4 text-emerald-500 mt-0.5" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium">{item.title}</p>
                      {item.due_date && (
                        <p className="text-xs text-muted-foreground">
                          Due: {format(parseISO(item.due_date), 'MMM d, yyyy')}
                        </p>
                      )}
                      {item.value && (
                        <p className="text-xs text-emerald-600 font-medium">
                          ${item.value.toLocaleString()}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {item.status || item.type}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckSquare className="h-10 w-10 mx-auto text-slate-200 mb-3" />
                <p className="text-sm text-muted-foreground">
                  No open items with this contact
                </p>
              </div>
            )}
          </ScrollArea>
        </TabsContent>
      </Tabs>

      {/* Relationship Summary */}
      {relationship_summary && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="h-4 w-4 text-emerald-600" />
              <span className="font-medium text-sm">Relationship Summary</span>
            </div>
            <p className="text-sm text-muted-foreground">{relationship_summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Refresh Button */}
      <div className="flex justify-center">
        <Button variant="ghost" size="sm" onClick={fetchMeetingPrep}>
          <RefreshCw className="h-3 w-3 mr-1" />
          Refresh Prep
        </Button>
      </div>
    </div>
  );
};

// Sheet Wrapper for Meeting Prep Button
export const MeetingPrepButton = ({ meeting, contactId, variant = 'outline', size = 'sm' }) => {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant={variant} size={size} data-testid="meeting-prep-btn">
          <Sparkles className="h-3 w-3 mr-1 text-purple-500" />
          Meeting Prep
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            Smart Meeting Prep
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4">
          <SmartMeetingPrep 
            meeting={meeting} 
            contactId={contactId}
            onClose={() => setOpen(false)}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SmartMeetingPrep;
